/**
 * teamSlotLogic — the pure half of the slot picker's Team mode (owner ask
 * 2026-10-16, the recruiters' "for scheduling" half of the Team day view): ONE
 * booking day with a column per recruiter on the lead's study roster (or the
 * whole team), so a free spot in anyone's column can be picked, and the pick
 * sets both the recruiter and the time.
 *
 * The columns, the hours and the lanes come from My Calendar's Team day
 * helpers (components/calendar/teamDayLogic.ts); the cells and the overlap rule
 * are the Week view's (slotLogic.ts dayCells / overlapsFor), so a pick's
 * conflicts and its Override behave exactly as they do there.
 */
import { TeamMember } from '../../types';
import { TeamColumn, gridHours, layoutLanes, teamColumns } from '../calendar/teamDayLogic';
import { Busy, BookingWindow, Conflict, GridBounds, GridCell, SLOT_STEP_MIN, addDays, gridBounds, minuteInDay, placeBlock, startOfDay } from './slotLogic';

/** Whose columns Team mode shows: the lead's study roster, or everyone in the CRM. */
export type TeamSource = 'roster' | 'team';

/** A busy block placed in a day column: minutes from the grid's first row, and its lane. */
export interface LaidBusy {
  busy: Busy;
  top: number;
  height: number;
  lane: number;
  lanes: number;
}

/**
 * The Team mode's columns: the roster's recruiters when one is given (and not
 * empty), else the whole team; plus the form's recruiter when they are on
 * neither, so the person the call is with now always has a column. Ordered as
 * My Calendar's Team day orders them (you first, then by name). A name the team
 * list lacks comes from `names` (the roster read carries them), else
 * "Current recruiter" for the form's own, else "Teammate".
 */
export function teamPickerColumns(
  team: TeamMember[],
  opts: { rosterIds: string[] | null; current: string | null; meId: string | undefined; names?: Map<string, string> },
): TeamColumn[] {
  const base = opts.rosterIds && opts.rosterIds.length ? opts.rosterIds : team.map((m) => m.id);
  const ids = opts.current && !base.includes(opts.current) ? [...base, opts.current] : base;
  const known = new Set(team.map((m) => m.id));
  const cols = teamColumns(team, { all: false, ids }, opts.meId, []).map((c) => {
    if (!c.id || known.has(c.id)) return c;
    const name = opts.names?.get(c.id) || (c.id === opts.current ? (c.isMe ? 'You' : 'Current recruiter') : c.name);
    return { ...c, name };
  });
  // teamColumns sorted by its own names; re-sort once the fallbacks are in.
  return cols.sort((a, b) => (a.isMe === b.isMe ? a.name.localeCompare(b.name) : a.isMe ? -1 : 1));
}

/** The `from`/`to` ISO pair covering one local day (one availability read per recruiter). */
export function dayRange(day: Date): { from: string; to: string } {
  const from = startOfDay(day);
  return { from: from.toISOString(), to: addDays(from, 1).toISOString() };
}

/**
 * The Team grid's rows: the booking window's own grid (business hours plus an
 * hour each side, as the Week view draws it) stretched by Team day's
 * `gridHours` to every timed busy block on screen. A block that covers the
 * whole day (an all-day event) is drawn but never stretches the grid.
 */
export function teamGridBounds(w: BookingWindow, busy: Busy[], day: Date): GridBounds {
  const base = gridBounds(w);
  const timed: { startMin: number; endMin: number }[] = [{ startMin: base.startMin, endMin: base.endMin }];
  for (const b of busy) {
    const s = Date.parse(b.start);
    const e = Date.parse(b.end);
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
    const startMin = minuteInDay(s, day);
    const endMin = minuteInDay(e, day);
    if (endMin <= startMin || (startMin <= 0 && endMin >= 24 * 60)) continue;
    timed.push({ startMin, endMin });
  }
  const { startHour, endHour } = gridHours(timed);
  const startMin = startHour * 60;
  const endMin = endHour * 60;
  return { startMin, endMin, rows: Math.round((endMin - startMin) / SLOT_STEP_MIN) };
}

/** One column's busy blocks, clipped to the grid and laid side by side where they overlap (Team day's lanes). */
export function busyLanes(busy: Busy[], day: Date, bounds: GridBounds, titleOf: (b: Busy) => string): LaidBusy[] {
  const timed: { item: { title: string; busy: Busy }; startMin: number; endMin: number }[] = [];
  for (const b of busy) {
    const at = placeBlock(Date.parse(b.start), Date.parse(b.end), day, bounds);
    if (at) timed.push({ item: { title: titleOf(b), busy: b }, startMin: at.top, endMin: at.top + at.height });
  }
  return layoutLanes(timed).map((p) => ({
    busy: p.item.busy,
    top: p.startMin,
    height: p.endMin - p.startMin,
    lane: p.lane,
    lanes: p.lanes,
  }));
}

/**
 * A click on a cell in a recruiter's column: the recruiter AND the time, with
 * what the cell said it collides with. Null for a past cell (not selectable),
 * or the "Unassigned" column (nobody to book with).
 */
export function teamPick(cell: GridCell, columnId: string | null): { assignee: string; ms: number; conflicts: Conflict[] } | null {
  if (!columnId || cell.state === 'past') return null;
  return { assignee: columnId, ms: cell.startMs, conflicts: cell.conflicts };
}
